import isString from 'lodash/isString';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import allowedTags from '@Services/AllowedTag';

import { ButtonWithIcon } from '@els/els-react--button';
import Button from '@Components/Button';
import { Checkbox } from '@els/els-react--checkbox';
import { ALLOWED_TAGS_ARRAY } from '../TreeViewConstants';

import './TreeViewListItem.scss';

// Prevent warning from Leyden
Checkbox.propTypes.label = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

function TreeViewListItem({ taxonomy, maxLineOfCount, onToggle, onSelect, showCountIndicator }) {
  const [tooltip, setTooltip] = useState('');

  useEffect(() => {
    const updatedTooltip = taxonomy.name !== 'Other targets' ? '' : 'Targets not yet assigned to a target class';
    setTooltip(updatedTooltip);
  }, [taxonomy]);

  const getPadding = () => {
    const paddingValue = taxonomy.nestingLevel ? `${taxonomy.nestingLevel}rem` : 0;
    return { paddingLeft: paddingValue };
  };

  const getNavigationArrowClass = () => {
    const icons = {
      OPENED: 'ChevronDown',
      CLOSED: 'ChevronRight',
    };
    return taxonomy.expanded && !taxonomy.hasHiddenChildren ? icons.OPENED : icons.CLOSED;
  };

  const getFolderIcon = () => {
    const icons = {
      0: 'folder-empty',
      1: 'folder-half',
      2: 'folder-full',
    };
    return icons[taxonomy.state || 0];
  };

  const getAllowedTags = (value) => {
    return allowedTags(value, ALLOWED_TAGS_ARRAY);
  };

  const getHitCountPercentage = () => {
    return `${((taxonomy.hitCount / maxLineOfCount) * 100).toFixed(1)}%`;
  };

  const convertToNumber = (hitCount) => {
    if (!hitCount) {
      return null;
    }
    if (isString(hitCount)) {
      return hitCount.replace(/(.)(?=(\d{3})+$)/g, '$1,');
    }
    return String(hitCount.toFixed().toLocaleString()).replace(/(.)(?=(\d{3})+$)/g, '$1,');
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(taxonomy);
  };

  const handleSelect = () => {
    onSelect(taxonomy);
  };

  const getContent = () => {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        className="treeViewListItem__item_content"
        onClick={taxonomy.isFolder ? handleSelect : undefined}
        data-testid="tree-view-list-item-content">
        <span className="treeViewListItem__item_content_head">{getAllowedTags(taxonomy.label.first)}</span>
        <span className="treeViewListItem__item_content_tail">{getAllowedTags(taxonomy.label.last)}</span>
        {taxonomy.hitSynonym && (
          <>
            <span className="treeViewListItem__item_content_head-synonym">{getAllowedTags(taxonomy.hitSynonym.first)}</span>
            <span className="treeViewListItem__item_content_tail-synonym">{getAllowedTags(taxonomy.hitSynonym.last)}</span>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="tree-view-list-item"
      className={classnames('treeViewListItem', {
        'treeViewListItem-final': !taxonomy.isFolder,
      })}>
      <div
        data-testid="tree-view-list-item-content-wrapper"
        className="treeViewListItem__item"
        title={taxonomy.nameSynonyms ? taxonomy.nameSynonyms.join('; ') : tooltip}
        style={getPadding()}>
        {taxonomy.isFolder && (
          <>
            <div className="treeViewListItem__item_navigate" data-testid="tree-view-list-item-navigate">
              <ButtonWithIcon
                className="e2e-tree-view-list-item-toggle"
                sprite={getNavigationArrowClass()}
                linkIconSize="xs"
                iconOnly
                type="link"
                onClick={handleToggle}
                data-testid="tree-view-list-item-navigate-button">
                Expand/Collapse taxonomy
              </ButtonWithIcon>
            </div>
            <div
              className={classnames('treeViewListItem__item_select', {
                'treeViewListItem__item_select-selected': taxonomy.state === 1 || taxonomy.state === 2,
              })}
              data-testid="tree-view-list-item-select">
              <Button
                onClick={handleSelect}
                type="button"
                buttonConfig={{
                  type: 'link',
                  size: 'sm',
                  e2eName: 'tree-view-list-item-selectFolder',
                  iconOnly: true,
                }}
                iconConfig={{
                  name: getFolderIcon(),
                }}
                data-testid="tree-view-list-item-select-button"
              />
            </div>
            {getContent()}
          </>
        )}

        {!taxonomy.isFolder && (
          <Checkbox
            label={getContent()}
            onChange={handleSelect}
            checked={taxonomy.selected}
            id={`taxonomy-${taxonomy.id}`}
            tabIndex={0}
            ariaControlsGuidance={getContent()}
          />
        )}
      </div>

      <div className="treeViewListItem__count">
        <div className="treeViewListItem__count_indicator">
          {showCountIndicator && (
            <>
              <div className="treeViewListItem__count_indicator-full" />
              <div
                className={classnames('treeViewListItem__count_indicator-full treeViewListItem__count_indicator-current', {
                  'treeViewListItem__count_indicator-current-selected': taxonomy.state === 1 || taxonomy.state === 2,
                })}
                style={{ width: getHitCountPercentage() }}
                data-testid="tree-view-list-item-count-indicator-current"
              />
            </>
          )}
        </div>
        <span className="treeViewListItem__count_badge" data-testid="tree-view-list-item-count-badge">
          {convertToNumber(taxonomy.hitCount)}
        </span>
      </div>
    </div>
  );
}

TreeViewListItem.defaultProps = {
  showCountIndicator: true,
};

TreeViewListItem.propTypes = {
  showCountIndicator: PropTypes.bool,
  taxonomy: PropTypes.object,
  maxLineOfCount: PropTypes.number,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default TreeViewListItem;
